'use client';

import * as React from 'react';
import { Clapperboard, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const VIDEO_SRC = '/videos/store-score-demo.mp4';
const POSTER_SRC = '/videos/store-score-demo-poster.jpg';
const VIDEO_DURATION = '1:30';

/**
 * Tutorial entry point shown under the score panel: a poster thumbnail that opens
 * the walkthrough in a dialog. The heavy source video only mounts when the dialog
 * opens, so the checklist page loads just the lightweight poster.
 */
export function QuickStartDemoCard() {
  const { t } = useTranslation('setup');
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Card className="gap-0 overflow-hidden py-0">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('quickStart.demo.play')}
          className="group focus-visible:ring-primary/50 block w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2"
        >
          <div className="relative aspect-video overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element -- Vite SPA, next/image is unavailable */}
            <img
              src={POSTER_SRC}
              alt=""
              className="size-full object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent transition-colors duration-300 group-hover:from-black/55" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="text-primary flex size-12 items-center justify-center rounded-full bg-white/95 shadow-lg transition-all duration-300 ease-out group-hover:bg-primary group-hover:text-white motion-safe:group-hover:scale-110">
                <Play className="size-5 translate-x-px fill-current" strokeWidth={0} aria-hidden />
              </span>
            </div>
            <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
              {VIDEO_DURATION}
            </span>
          </div>

          <div className="flex items-start gap-2.5 px-4 py-3">
            <Clapperboard className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wider">
                {t('quickStart.demo.eyebrow')}
              </p>
              <p className="text-foreground mt-0.5 text-sm font-medium leading-snug">
                {t('quickStart.demo.title')}
              </p>
            </div>
          </div>
        </button>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('quickStart.demo.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('quickStart.demo.dialogSubtitle')}</DialogDescription>
          </DialogHeader>
          <video
            className="aspect-video w-full overflow-hidden rounded-lg border bg-black"
            src={VIDEO_SRC}
            poster={POSTER_SRC}
            controls
            autoPlay
            muted
            playsInline
            preload="metadata"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
